/**
 * FS-10 나의 발자취 — 순수 로직(시험 대상). 본인 기록만 · 비교·평균·순위·'최고 기록' 없음(SR-REC-03 · DR-01).
 * 행 = 도전 한 번(스냅샷 버전 번호 · 코스 · 완주/미완주 · 내 시간 · 시도 · 리플레이). 버전 번호는 내 스냅샷을 만든 순서(v1…).
 * 정렬·필터는 내 기록 안에서만(SU 와이어 '정렬: 최근순 ▾ (내 기록 안에서만)' · '필터: 코스 ▾').
 */
import type { Footprints, FootprintRow, SortOrder } from "../types";

const ts = (s: string) => {
  const v = Date.parse(s);
  return Number.isFinite(v) ? v : 0;
};

export function footprintRows(fp: Footprints): FootprintRow[] {
  const versions = [...fp.versions].sort(
    (a, b) => ts(a.createdAt) - ts(b.createdAt) || a.snapshotId.localeCompare(b.snapshotId),
  );
  const rows: FootprintRow[] = [];
  versions.forEach((v, i) => {
    for (const a of v.attempts)
      rows.push({
        attemptId: a.attemptId,
        version: i + 1,
        snapshotId: v.snapshotId,
        current: v.current,
        presetKey: a.presetKey,
        completed: a.outcome === "complete",
        timeMs: a.outcome === "complete" ? a.durationMs : null,
        tries: a.tries,
        failPattern: a.failPattern,
        replayId: a.replayId,
        createdAt: a.createdAt,
      });
  });
  return sortRows(rows, "recent");
}

export function sortRows(rows: readonly FootprintRow[], order: SortOrder): FootprintRow[] {
  const dir = order === "recent" ? -1 : 1;
  return [...rows].sort(
    (a, b) =>
      dir * (ts(a.createdAt) - ts(b.createdAt)) ||
      dir * (a.version - b.version) ||
      a.attemptId.localeCompare(b.attemptId),
  );
}

export function filterRows(
  rows: readonly FootprintRow[],
  presetKey: string | null,
): FootprintRow[] {
  return presetKey ? rows.filter((r) => r.presetKey === presetKey) : [...rows];
}

/** 기록에 나온 코스(프리셋) — 필터 목록(순서: 쉬움 · 보통 · 어려움 · 그 밖) */
export function presetsIn(rows: readonly FootprintRow[]): string[] {
  const order = ["easy", "medium", "hard"];
  const keys = [...new Set(rows.map((r) => r.presetKey).filter(Boolean))];
  return keys.sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
}

/**
 * 버전이 바뀌며 달라진 점 하나(SU 안내 'v3→v4에서 출발 속도를 낮추자 보통 코스를 완주했어요') —
 * 같은 코스에서 앞 버전은 미완주, 뒤 버전은 완주한 가장 최근 경우. 앞 버전 실패 패턴이 있으면 속도·출력 어휘로 무엇을 바꿨는지 말한다.
 * 남과 견주지 않는다(내 버전끼리만).
 */
export function versionInsight(
  rows: readonly FootprintRow[],
): { from: number; to: number; presetKey: string; failPattern: string | null } | null {
  const done = sortRows(
    rows.filter((r) => r.completed),
    "recent",
  );
  for (const d of done) {
    const before = sortRows(
      rows.filter((r) => !r.completed && r.presetKey === d.presetKey && r.version < d.version),
      "recent",
    );
    // 같은 코스를 이 버전보다 앞 버전에서 완주한 적이 있으면 '달라진 점'이 아니다
    const doneBefore = rows.some(
      (r) => r.completed && r.presetKey === d.presetKey && r.version < d.version,
    );
    const prev = before[0];
    if (prev && !doneBefore)
      return {
        from: prev.version,
        to: d.version,
        presetKey: d.presetKey,
        failPattern: prev.failPattern,
      };
  }
  return null;
}

/** 'MM-DD'(SU 날짜 칸) */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
